import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID, inject } from '@angular/core';
import { HomeDashboardService } from '../../data/services/view/home-dashboard.service';
import { DataTableService } from '@data/services/utils/data-table.service';
import { LoaderService } from '@data/services/page/loader.service';
import { MessageService } from 'primeng/api';
import { 
  MonthlyLeaseBalancesResponse,
  LeaseMonthlyBalances,
  MonthlySaldoDetail,
  MonthlyBalancesState,
  MonthColumn,
  ProcessedLeaseData
} from '../../data/interfaces/monthly-balances.interface';

@Component({
  selector: 'app-monthly-balances',
  templateUrl: './monthly-balances.component.html',
  styleUrls: ['./monthly-balances.component.scss']
})
export class MonthlyBalancesComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  platformId = inject(PLATFORM_ID);

  // State
  state: MonthlyBalancesState = {
    data: null,
    loading: false,
    selectedLease: null,
    selectedPeriod: null,
    selectedMonth: null,
    filters: {
      search: ''
    },
    view: 'table'
  };

  // Processed data
  processedLeases: ProcessedLeaseData[] = [];
  filteredLeases: ProcessedLeaseData[] = [];
  monthColumns: MonthColumn[] = [];

  // Monthly totals for footer
  monthlyTotals: { [monthKey: string]: number } = {};

  // UI State
  displayDetailModal: boolean = false;
  globalFilter: string = '';

  constructor(
    private dashboardService: HomeDashboardService,
    private dataTableService: DataTableService,
    private loaderService: LoaderService,
    private messageService: MessageService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadData() {
    this.state.loading = true;
    
    this.dashboardService.monthlyLeaseBalances()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('Monthly Lease Balances Response:', response);
          this.state.data = response.data;
          this.processData();
          this.state.loading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading monthly lease balances:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los saldos mensuales'
          });
          this.state.loading = false;
          this.cdr.detectChanges();
        }
      });
  }

  private processData() {
    if (!this.state.data) return;

    // Generate month columns from the data
    this.generateMonthColumns();
    
    // Process leases data
    this.processedLeases = this.state.data.arrendamientos.map(lease => this.processLease(lease));
    
    // Apply filters
    this.applyFilters();
  }

  private generateMonthColumns() {
    if (!this.state.data) return;

    const monthsSet = new Set<string>();
    
    // Collect all unique months from all leases
    this.state.data.arrendamientos.forEach(lease => {
      lease.saldosPorMes.forEach(saldo => {
        monthsSet.add(saldo.mes);
      });
    });

    // Convert to sorted array and create month columns
    // Sort months by a logical order (could be improved with actual date sorting)
    const monthsArray = Array.from(monthsSet).sort((a, b) => {
      const monthOrder = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 
                         'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
      const aIndex = monthOrder.findIndex(m => a.toLowerCase().includes(m));
      const bIndex = monthOrder.findIndex(m => b.toLowerCase().includes(m));
      return aIndex - bIndex;
    });
    
    this.monthColumns = monthsArray.map(month => ({
      key: month.toLowerCase().replace(/\s+/g, '-'),
      label: month.substring(0, 3), // First 3 letters
      fullName: month
    }));
  }

  private processLease(lease: LeaseMonthlyBalances): ProcessedLeaseData {
    const monthlyPayments: { [monthKey: string]: MonthlySaldoDetail | null } = {};
    
    // Initialize all months as null
    this.monthColumns.forEach(month => {
      monthlyPayments[month.key] = null;
    });

    // Fill with actual data
    lease.saldosPorMes.forEach(saldo => {
      const monthKey = saldo.mes.toLowerCase().replace(/\s+/g, '-');
      monthlyPayments[monthKey] = saldo;
    });

    // Calculate totals
    const totalPendiente = lease.saldosPorMes.reduce((sum, saldo) => 
      sum + (saldo.montoPorPagar - saldo.montoPagado), 0);
    
    const totalPagado = lease.saldosPorMes.reduce((sum, saldo) => 
      sum + saldo.montoPagado, 0);

    return {
      ...lease,
      monthlyPayments,
      totalPendiente,
      totalPagado,
      hasPartialPayments: false, // Mantenemos para compatibilidad pero no se usa
      allPaid: false // Mantenemos para compatibilidad pero no se usa
    };
  }

  // Filter and search methods
  applyFilters() {
    this.filteredLeases = this.processedLeases.filter(lease => {
      // Search filter - buscar tanto en folio como en nombre de cliente
      if (this.state.filters.search) {
        const searchTerm = this.state.filters.search.toLowerCase();
        const folioMatch = lease.folio.toLowerCase().includes(searchTerm);
        const clientMatch = lease.client.toLowerCase().includes(searchTerm);
        
        if (!folioMatch && !clientMatch) {
          return false;
        }
      }

      return true;
    });

    // Actualizar totales mensuales después del filtrado
    this.updateMonthlyTotals();
  }

  // Calcular totales mensuales para el footer
  private calculateMonthlyTotals(): { [monthKey: string]: number } {
    const totals: { [monthKey: string]: number } = {};
    
    // Inicializar totales en 0 para cada mes
    this.monthColumns.forEach(month => {
      totals[month.key] = 0;
    });
    
    // Sumar montoPorPagar de cada lease por mes
    this.filteredLeases.forEach(lease => {
      this.monthColumns.forEach(month => {
        const payment = lease.monthlyPayments[month.key];
        if (payment) {
          totals[month.key] += payment.montoPorPagar;
        }
      });
    });
    
    return totals;
  }

  // Actualizar totales mensuales
  private updateMonthlyTotals(): void {
    this.monthlyTotals = this.calculateMonthlyTotals();
  }

  onGlobalFilterChange() {
    this.state.filters.search = this.globalFilter;
    this.applyFilters();
  }

  // Cell interaction methods
  getPaymentStatus(lease: ProcessedLeaseData, monthKey: string): string {
    const payment = lease.monthlyPayments[monthKey];
    
    if (!payment) return 'no-period';
    
    // Verificar si es pago anticipado
    if (payment.estatus === 'ANTICIPATED') return 'paid-anticipated';
    
    if (payment.montoPagado === payment.montoPorPagar) return 'paid-complete';
    if (payment.montoPagado > 0) return 'paid-partial';
    return 'unpaid';
  }

  getPaymentIcon(lease: ProcessedLeaseData, monthKey: string): string {
    const status = this.getPaymentStatus(lease, monthKey);
    
    switch (status) {
      case 'paid-complete': return '✅';
      case 'paid-partial': return '⚠️';
      case 'paid-anticipated': return '💰';
      case 'unpaid': return '❌';
      default: return '⚪';
    }
  }

  showDetail(lease: ProcessedLeaseData, monthKey: string) {
    const payment = lease.monthlyPayments[monthKey];
    
    if (!payment) return;

    this.state.selectedLease = lease;
    this.state.selectedPeriod = payment;
    this.state.selectedMonth = this.monthColumns.find(m => m.key === monthKey)?.fullName || monthKey;
    this.displayDetailModal = true;
  }

  getSaldoPendiente(): number {
    if (!this.state.selectedPeriod) return 0;
    return this.state.selectedPeriod.montoPorPagar - this.state.selectedPeriod.montoPagado;
  }

  // Método para obtener severity del estado de un período
  getPeriodiStatusSeverity(periodo: MonthlySaldoDetail): string {
    if (periodo.estatus === 'ANTICIPATED') return 'info';
    if (periodo.montoPagado === periodo.montoPorPagar) return 'success';
    if (periodo.montoPagado > 0) return 'warning';
    return 'danger';
  }

  // Método para obtener texto del estado de un período  
  getPeriodiStatusText(periodo: MonthlySaldoDetail): string {
    if (periodo.estatus === 'ANTICIPATED') return 'Renta Anticipada';
    if (periodo.montoPagado === periodo.montoPorPagar) return 'Pagado';
    if (periodo.montoPagado > 0) return 'Parcial';
    return 'Pendiente';
  }

  // Action methods
  refresh() {
    this.loadData();
  }

  exportToExcel() {
    if (!this.filteredLeases.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'No hay datos para exportar'
      });
      return;
    }

    // Prepare data for export - Data rows
    const exportData = this.filteredLeases.map(lease => {
      const row: any = { 
        'ID': lease.oldId,
        'Folio': lease.folio,
        'Cliente': lease.client
      };
      
      // Calculate total expected amount for this lease
      let totalEsperado = 0;
      
      this.monthColumns.forEach(month => {
        const payment = lease.monthlyPayments[month.key];
        if (payment) {
          row[month.fullName] = `$${payment.montoPorPagar.toLocaleString()}`;
          totalEsperado += payment.montoPorPagar;
        } else {
          row[month.fullName] = '-';
        }
      });

      row['Total Esperado'] = `$${totalEsperado.toLocaleString()}`;
      
      return row;
    });

    // Add totals row
    const totalsRow: any = {
      'ID': '',
      'Folio': 'TOTALES MENSUALES',
      'Cliente': ''
    };

    let grandTotal = 0;
    this.monthColumns.forEach(month => {
      const monthTotal = this.monthlyTotals[month.key] || 0;
      totalsRow[month.fullName] = `$${monthTotal.toLocaleString()}`;
      grandTotal += monthTotal;
    });

    totalsRow['Total Esperado'] = `$${grandTotal.toLocaleString()}`;
    
    // Add totals row to export data
    exportData.push(totalsRow);

    // Generate filename with date range
    let filename = 'saldos-mensuales-arrendamientos';
    if (this.state.data?.rangoAnalisis) {
      const startDate = new Date(this.state.data.rangoAnalisis.fechaInicio);
      const endDate = new Date(this.state.data.rangoAnalisis.fechaFin);
      const startMonth = startDate.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' });
      const endMonth = endDate.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' });
      filename += `_${startMonth.replace('.', '')}_a_${endMonth.replace('.', '')}`;
    }

    this.dataTableService.exportExcel(exportData, filename);

    this.messageService.add({
      severity: 'success',
      summary: 'Exportado',
      detail: `Reporte de saldos mensuales exportado exitosamente con ${this.filteredLeases.length} arrendamientos`
    });
  }

  navigateToLease(lease: ProcessedLeaseData) {
    // Navigate to lease detail using the same pattern as pending-balances
    if (lease.id) {
      this.router.navigate(['/lease', lease.id]);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'No se encontró el ID del arrendamiento',
        life: 3000
      });
    }
  }

  trackByLeaseId(index: number, lease: ProcessedLeaseData): string {
    return lease.oldId;
  }

  getTooltipText(lease: ProcessedLeaseData, monthKey: string): string {
    const payment = lease.monthlyPayments[monthKey];
    
    if (!payment) return 'Sin período para este mes';
    
    if (payment.estatus === 'ANTICIPATED') {
      return `Pago Anticipado: $${payment.montoPagado.toLocaleString()} / Total: $${payment.montoPorPagar.toLocaleString()}`;
    }
    
    return `Pagado: $${payment.montoPagado.toLocaleString()} / Total: $${payment.montoPorPagar.toLocaleString()}`;
  }

  showLeaseDetail(lease: ProcessedLeaseData) {
    // Show all periods for this lease in a modal or navigate to detail
    this.state.selectedLease = lease;
    this.state.selectedPeriod = null; // Show all periods
    this.state.selectedMonth = null;
    this.displayDetailModal = true;
  }

  // Utility function to format amounts in compact form
  formatCompactAmount(amount: number): string {
    if (amount >= 1000000) {
      return (amount / 1000000).toFixed(1) + 'M';
    } else if (amount >= 1000) {
      return (amount / 1000).toFixed(0) + 'k';
    }
    return amount.toString();
  }

  // Format totals with full cents for footer
  formatTotalAmount(amount: number): string {
    return amount.toLocaleString('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  // Get severity for tags and styling
  getAmountSeverity(amount: number): string {
    if (amount === 0) return 'success';
    if (amount < 50000) return 'info';
    if (amount < 100000) return 'warning';
    return 'danger';
  }

  // Helper methods for selected lease totals
  getSelectedLeaseTotalPendiente(): number {
    if (!this.state.selectedLease) return 0;
    return this.state.selectedLease.totalPendiente;
  }

  getSelectedLeaseTotalPagado(): number {
    if (!this.state.selectedLease) return 0;
    return this.state.selectedLease.totalPagado;
  }

  navigateToSelectedLease() {
    if (this.state.selectedLease) {
      this.navigateToLease(this.state.selectedLease);
    }
  }

  // Métodos para funcionalidades futuras
  showRegisterPaymentMessage() {
    this.messageService.add({
      severity: 'info',
      summary: 'Próximamente',
      detail: 'La funcionalidad de Registrar Pago se agregará próximamente',
      life: 3000
    });
  }

  showViewHistoryMessage() {
    this.messageService.add({
      severity: 'info',
      summary: 'Próximamente', 
      detail: 'La funcionalidad de Ver Historial se agregará próximamente',
      life: 3000
    });
  }

  exportSelectedLeaseDetail() {
    if (!this.state.selectedLease) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'No hay arrendamiento seleccionado para exportar'
      });
      return;
    }

    // Preparar datos del arrendamiento seleccionado para exportar
    const exportData = this.state.selectedLease.saldosPorMes.map(periodo => ({
      'Folio': this.state.selectedLease!.folio,
      'Período': `#${periodo.periodo}`,
      'Mes': periodo.mes,
      'Monto por Pagar': `$${periodo.montoPorPagar.toLocaleString()}`,
      'Monto Pagado': `$${periodo.montoPagado.toLocaleString()}`,
      'Saldo Pendiente': `$${(periodo.montoPorPagar - periodo.montoPagado).toLocaleString()}`,
      'Estado': periodo.montoPagado === periodo.montoPorPagar ? 'Pagado' : 
                (periodo.montoPagado > 0 ? 'Parcial' : 'Pendiente')
    }));

    // Agregar fila de totales
    exportData.push({
      'Folio': 'TOTAL',
      'Período': '',
      'Mes': '',
      'Monto por Pagar': `$${this.state.selectedLease.saldosPorMes.reduce((sum, p) => sum + p.montoPorPagar, 0).toLocaleString()}`,
      'Monto Pagado': `$${this.getSelectedLeaseTotalPagado().toLocaleString()}`,
      'Saldo Pendiente': `$${this.getSelectedLeaseTotalPendiente().toLocaleString()}`,
      'Estado': ''
    });

    const filename = `detalle-${this.state.selectedLease.folio.replace(/[^a-zA-Z0-9]/g, '-')}`;
    this.dataTableService.exportExcel(exportData, filename);

    this.messageService.add({
      severity: 'success',
      summary: 'Exportado',
      detail: `Detalle del arrendamiento ${this.state.selectedLease.folio} exportado exitosamente`
    });
  }
}
